import React, {useEffect, useState} from 'react';
import {Switch} from "antd";
import {doc, getFirestore, setDoc} from "firebase/firestore";
import {useDocumentData} from "react-firebase-hooks/firestore";
import {getAuth} from "firebase/auth";
import {useAuthState} from "react-firebase-hooks/auth";
import Firebase from "../Firebase/Firebase";
import {currentSubscription, disableAdminPush, enableAdminPush, isIOS, isStandalone, pushSupported} from "./adminPush";

const db = getFirestore(Firebase);

export const ADMIN_CATEGORIES: {key: string; label: string; hint: string}[] = [
    {key: 'orders', label: 'Orders & payments', hint: 'Each paid order, with the amount'},
    {key: 'registrations', label: 'Registrations', hint: 'New users and new sellers'},
    {key: 'payouts', label: 'Payouts', hint: 'Seller payouts sent or failed'},
    {key: 'support', label: 'Support', hint: 'Messages to Shopped Support, contact forms, support payments'},
];

const box: React.CSSProperties = {background: '#f8fafc', border: '1px solid #eef2f7', borderRadius: 10, padding: 12, fontSize: 13, lineHeight: 1.45};
const btn: React.CSSProperties = {background: '#29327b', color: '#fff', border: 0, borderRadius: 8, padding: '8px 14px', fontWeight: 600, cursor: 'pointer'};

/** The admin's browser notifications: turn on for this device + which kinds. */
export default function AdminNotifications() {
    const [user] = useAuthState(getAuth(Firebase));
    const [prefs] = useDocumentData(user ? doc(db, 'adminNotificationPrefs', user.uid) : null);
    const [on, setOn] = useState<boolean | null>(null);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');

    useEffect(() => {
        currentSubscription().then((s) => setOn(!!s && Notification.permission === 'granted')).catch(() => setOn(false));
    }, []);

    const ios = isIOS();
    const needsInstall = ios && !isStandalone();

    const turnOn = async () => {
        if (!user) return;
        setBusy(true); setMsg('');
        try {
            const r = await enableAdminPush(user.uid);
            setOn(r === 'on');
            if (r === 'denied') setMsg('Notifications are blocked for this site. Allow them in your browser settings, then try again.');
            if (r === 'unsupported') setMsg('This browser cannot receive notifications.');
        } catch (e: any) {
            setMsg(e.message);
        } finally { setBusy(false); }
    };
    const turnOff = async () => { setBusy(true); await disableAdminPush(); setOn(false); setBusy(false); };

    const test = async () => {
        const reg = await navigator.serviceWorker.getRegistration();
        await reg?.showNotification('Shopped Admin', {body: 'Notifications are working on this device ✅', icon: '/admin-icon-192.png'});
    };

    return (
        <div style={{display: 'grid', gap: 14}}>
            {needsInstall ? (
                <div style={box}>
                    <b>On iPhone, add the admin panel to your Home Screen first</b> (Apple only allows notifications for apps opened from the Home Screen, iOS 16.4+):
                    <ol style={{margin: '8px 0 0', paddingLeft: 18}}>
                        <li>Tap the <b>Share</b> button (Safari: bottom bar · Chrome: top-right ⋯ then Share).</li>
                        <li>Choose <b>Add to Home Screen</b>, then <b>Add</b>.</li>
                        <li>Open <b>Shopped Admin</b> from your Home Screen and come back here.</li>
                    </ol>
                </div>
            ) : !pushSupported() ? (
                <div style={box}>This browser can't receive notifications. Try Chrome, Edge or Safari (iPhone: from the Home Screen).</div>
            ) : (
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap'}}>
                    <div>
                        <div style={{fontWeight: 700}}>{on ? 'On for this device' : 'Off on this device'}</div>
                        <div style={{fontSize: 12, color: '#888'}}>Each device (phone, laptop) is turned on separately.</div>
                    </div>
                    {on ? (
                        <div style={{display: 'flex', gap: 8}}>
                            <button style={{...btn, background: '#eef2ff', color: '#29327b'}} onClick={test}>Test</button>
                            <button style={{...btn, background: '#fff', color: '#b91c1c', border: '1px solid #fecaca'}} disabled={busy} onClick={turnOff}>Turn off</button>
                        </div>
                    ) : (
                        <button style={btn} disabled={busy || on === null} onClick={turnOn}>{busy ? 'Turning on…' : 'Turn on notifications'}</button>
                    )}
                </div>
            )}
            {msg && <div style={{color: '#b91c1c', fontSize: 13}}>{msg}</div>}

            <div>
                <div style={{fontWeight: 700, marginBottom: 6}}>Notify me about</div>
                {ADMIN_CATEGORIES.map((c) => (
                    <div key={c.key} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderTop: '1px solid #f3f4f6'}}>
                        <div>
                            <div style={{fontSize: 14}}>{c.label}</div>
                            <div style={{fontSize: 12, color: '#999'}}>{c.hint}</div>
                        </div>
                        <Switch
                            checked={prefs?.[c.key] !== false}
                            disabled={!user}
                            onChange={(v) => user && setDoc(doc(db, 'adminNotificationPrefs', user.uid), {[c.key]: v}, {merge: true})}
                        />
                    </div>
                ))}
                <div style={{fontSize: 12, color: '#999', marginTop: 4}}>These apply to all your devices.</div>
            </div>
        </div>
    );
}
